import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Fieldset, Link, Paragraph } from '@digdir/designsystemet-react';
import { SettingsSection } from '../../../components/TeamSettingsLayout';
import { Routes } from '../../../enums/Routes';
import { BusinessTextField, bind, SectionProps } from './BusinessTextField';
import classes from './OrganizationBusinessPage.module.css';

type Props = SectionProps & {
  /** Registered among the safety representatives, so it is not typed in twice */
  mainSafetyReps: string[];
};

/** Who is responsible, and who the Labour Inspection Authority contacts */
export const ContactsSection = ({ mainSafetyReps, ...props }: Props): ReactElement => {
  const { t } = useTranslation();
  return (
    <SettingsSection
      headingId="business-contacts"
      title={t('organizationBusiness.contacts.title')}
      description={t('organizationBusiness.contacts.description')}
    >
      <Fieldset className={classes.group}>
        <Fieldset.Legend>{t('organizationBusiness.contacts.ceo')}</Fieldset.Legend>
        <Fieldset.Description>{t('organizationBusiness.contacts.ceoHint')}</Fieldset.Description>
        <div className={classes.pair}>
          <BusinessTextField
            {...bind('ceoName', props)}
            label={t('organizationBusiness.contacts.name')}
            autoComplete="name"
          />
          <BusinessTextField
            {...bind('ceoEmail', props)}
            type="email"
            label={t('organizationBusiness.contacts.email')}
            autoComplete="email"
          />
        </div>
      </Fieldset>
      <Fieldset className={classes.group}>
        <Fieldset.Legend>{t('organizationBusiness.contacts.inspection')}</Fieldset.Legend>
        <Fieldset.Description>{t('organizationBusiness.contacts.inspectionHint')}</Fieldset.Description>
        <div className={classes.pair}>
          <BusinessTextField
            {...bind('inspectionContactName', props)}
            label={t('organizationBusiness.contacts.name')}
            autoComplete="name"
          />
          <BusinessTextField
            {...bind('inspectionContactRole', props)}
            label={t('organizationBusiness.contacts.role')}
          />
          <BusinessTextField
            {...bind('inspectionContactEmail', props)}
            type="email"
            label={t('organizationBusiness.contacts.email')}
            autoComplete="email"
          />
          <BusinessTextField
            {...bind('inspectionContactPhone', props)}
            type="tel"
            label={t('organizationBusiness.contacts.phone')}
            autoComplete="tel"
          />
        </div>
      </Fieldset>
      <div className={classes.group}>
        <span className="ds-label">{t('organizationBusiness.contacts.mainSafetyRep')}</span>
        <Paragraph data-size="xs" className={classes.hint}>
          {t('organizationBusiness.contacts.mainSafetyRepHint')}
        </Paragraph>
        <Paragraph data-size="sm">
          {mainSafetyReps.length > 0 ? mainSafetyReps.join(', ') : t('organizationBusiness.contacts.noMainSafetyRep')}
        </Paragraph>
        <Link asChild data-size="sm">
          <RouterLink to={Routes.HRSafetyRepsFullPath}>
            {t('organizationBusiness.contacts.changeSafetyReps')}
          </RouterLink>
        </Link>
      </div>
    </SettingsSection>
  );
};
